/**
 * 授权列表筛选栏（03-UIUX.md §6.4）。
 *
 * 两条容易被省掉、但省掉就违反规范的细节：
 * 1. 已应用的筛选**必须**以可删除 chips 显式呈现在筛选栏下方，不能只藏在下拉里。
 * 2. 搜索框 placeholder 必须诚实说明「能搜什么」—— 服务端不保存授权码明文，
 *    搜不到完整码不是 bug，但用户无从得知，除非我们写出来。
 */
import { forwardRef } from 'react';
import { Icon } from '../../icons/Icon';
import { StatusBadge } from '../../components/StatusBadge';
import { LICENSE_STATUSES } from '../../api/types';
import type { LicenseStatus, Product } from '../../api/types';
import { ICON_SIZE, statusOf } from '../../tokens';
import { formatCount } from '../../lib/format';
import type { LicenseFilters } from './useLicenseQuery';

export const SEARCH_PLACEHOLDER = '按批次号、授权码后 4 位、license_id、客户备注搜索';

export interface LicenseFilterBarProps {
  readonly filters: LicenseFilters;
  readonly products: readonly Product[];
  readonly statusCounts: Partial<Record<LicenseStatus, number>>;
  readonly hasActiveFilters: boolean;
  readonly onChange: (patch: Partial<LicenseFilters>) => void;
  readonly onClear: () => void;
}

export const LicenseFilterBar = forwardRef<HTMLInputElement, LicenseFilterBarProps>(
  function LicenseFilterBar(
    { filters, products, statusCounts, hasActiveFilters, onChange, onClear },
    searchRef,
  ) {
    const productName = (id: string) => products.find((p) => p.id === id)?.name ?? id;

    return (
      <>
        <div className="filter-bar">
          <label className="search-field">
            <Icon name="search" size={ICON_SIZE.inline} />
            <input
              ref={searchRef}
              type="search"
              value={filters.q}
              placeholder={SEARCH_PLACEHOLDER}
              aria-label={SEARCH_PLACEHOLDER}
              onChange={(event) => onChange({ q: event.target.value })}
            />
          </label>

          <div className="status-filters" role="group" aria-label="按状态筛选">
            {LICENSE_STATUSES.map((status) => {
              const descriptor = statusOf(status);
              const selected = filters.status === status;
              const count = statusCounts[status];
              return (
                <button
                  key={status}
                  type="button"
                  className="status-chip"
                  aria-pressed={selected}
                  onClick={() => onChange({ status: selected ? undefined : status })}
                >
                  <Icon name={descriptor.icon} size={ICON_SIZE.badge} />
                  {descriptor.label}
                  {count === undefined ? null : (
                    <span className="status-chip__count">{formatCount(count)}</span>
                  )}
                </button>
              );
            })}
          </div>

          <label className="cell-inline" style={{ color: 'var(--muted)' }}>
            产品
            <select
              value={filters.productId ?? ''}
              onChange={(event) =>
                onChange({ productId: event.target.value === '' ? undefined : event.target.value })
              }
              aria-label="按产品筛选"
              style={{
                height: '32px',
                border: '1px solid var(--border-control)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface)',
                color: 'var(--fg)',
                font: 'inherit',
                padding: '0 var(--space-1)',
              }}
            >
              <option value="">全部产品</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {hasActiveFilters ? (
          <div className="applied-filters">
            <span>已筛选：</span>

            {filters.q === '' ? null : (
              <button type="button" className="filter-tag" onClick={() => onChange({ q: '' })}>
                关键词：{filters.q}
                <Icon name="x" size={ICON_SIZE.badge} />
              </button>
            )}

            {filters.status === undefined ? null : (
              <button
                type="button"
                className="filter-tag"
                onClick={() => onChange({ status: undefined })}
              >
                状态：<StatusBadge status={filters.status} />
                <Icon name="x" size={ICON_SIZE.badge} />
              </button>
            )}

            {filters.productId === undefined ? null : (
              <button
                type="button"
                className="filter-tag"
                onClick={() => onChange({ productId: undefined })}
              >
                产品：{productName(filters.productId)}
                <Icon name="x" size={ICON_SIZE.badge} />
              </button>
            )}

            {filters.expiringSoon ? (
              <button
                type="button"
                className="filter-tag"
                onClick={() => onChange({ expiringSoon: false })}
              >
                7 天内到期
                <Icon name="x" size={ICON_SIZE.badge} />
              </button>
            ) : null}

            <button type="button" className="link-button" onClick={onClear}>
              清除全部
            </button>
          </div>
        ) : null}
      </>
    );
  },
);
